import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
} from '@roger/ui-components';
import { Bell, Loader2, Sparkles } from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import { FeatureFlag } from '../constants/featureFlags';
import { useToast } from '../hooks/useToastContext';
import { useStore } from '../store';
import { MarkdownContent } from './MarkdownContent';
import {
  getProductUpdatesForUser,
  productUpdateRowKey,
  type ProductUpdateData,
} from '../services/productUpdates';
import {
  filterUnviewedProductUpdates,
  markProductUpdatesViewed,
} from '../utils/productUpdateViews';

const ProductUpdatesButton: React.FC = () => {
  const { user, isModalEnabled } = useStore(
    useShallow(state => ({
      user: state.user,
      isModalEnabled: state.hasFeatureFlag(
        FeatureFlag.PRODUCT_UPDATE_MODAL_ENABLED
      ),
    }))
  );
  const [open, setOpen] = useState(false);
  const [updates, setUpdates] = useState<ProductUpdateData[]>([]);
  const [imageUrls, setImageUrls] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  /** null = not yet loaded; false = API returned no active updates (e.g. all expired). */
  const [hasAvailableUpdates, setHasAvailableUpdates] = useState<
    boolean | null
  >(null);
  const { error: showError } = useToast();
  const loginAutoOpenAttempted = useRef(false);

  const applyLoadedPayload = useCallback(
    (
      rows: ProductUpdateData[],
      urls: Record<string, string>,
      allFromApi: ProductUpdateData[]
    ) => {
      setUpdates(rows);
      setImageUrls(urls);
      if (rows.length > 0 && user?.id) {
        markProductUpdatesViewed(rows, user.id, allFromApi);
      }
    },
    [user?.id]
  );

  const loadUpdates = useCallback(
    async (options: { unviewedOnly?: boolean } = {}) => {
      if (!isModalEnabled || !user?.id) {
        return;
      }
      setLoading(true);
      try {
        const { updates: rows, image_urls } = await getProductUpdatesForUser();
        setHasAvailableUpdates(rows.length > 0);
        const toShow = options.unviewedOnly
          ? filterUnviewedProductUpdates(rows, user.id)
          : rows;
        applyLoadedPayload(toShow, image_urls ?? {}, rows);
      } catch (e) {
        console.error('Failed to load product updates:', e);
        showError(
          'Unable to Load Updates',
          'Something went wrong. Please try again.'
        );
        setOpen(false);
      } finally {
        setLoading(false);
      }
    },
    [applyLoadedPayload, isModalEnabled, showError, user?.id]
  );

  useEffect(() => {
    if (!isModalEnabled || !user?.id) {
      setHasAvailableUpdates(null);
      loginAutoOpenAttempted.current = false;
      return;
    }
    if (loginAutoOpenAttempted.current) {
      return;
    }
    loginAutoOpenAttempted.current = true;

    void (async () => {
      try {
        const { updates: rows, image_urls } = await getProductUpdatesForUser();
        setHasAvailableUpdates(rows.length > 0);
        const unviewed = filterUnviewedProductUpdates(rows, user.id);
        if (unviewed.length === 0) {
          return;
        }
        applyLoadedPayload(unviewed, image_urls ?? {}, rows);
        setOpen(true);
      } catch (e) {
        console.error('Failed to check product updates on login:', e);
        setHasAvailableUpdates(true);
      }
    })();
  }, [applyLoadedPayload, isModalEnabled, user?.id]);

  if (!isModalEnabled || hasAvailableUpdates !== true) {
    return null;
  }

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (nextOpen) {
      void loadUpdates();
    }
  };

  return (
    <>
      <Button
        type='button'
        variant='outline'
        size='lg'
        onClick={() => handleOpenChange(true)}
        aria-label="What's New"
      >
        <Bell aria-hidden='true' />
        <span className='hidden sm:inline'>What&apos;s New</span>
      </Button>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className='flex max-h-[90vh] max-w-lg flex-col gap-0 overflow-hidden p-0 shadow-xl sm:max-w-lg'>
          <div className='relative shrink-0 overflow-hidden border-b px-6 pb-5 pt-6'>
            <div
              className='pointer-events-none absolute inset-0 bg-gradient-to-br from-primary/10 via-primary/[0.03] to-transparent'
              aria-hidden='true'
            />
            <DialogHeader className='relative space-y-0 text-left'>
              <div className='flex items-start gap-3.5 pr-8'>
                <div className='flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 ring-1 ring-primary/15'>
                  <Bell className='h-5 w-5 text-primary' aria-hidden='true' />
                </div>
                <div className='min-w-0 space-y-1'>
                  <DialogTitle className='text-xl font-semibold tracking-tight'>
                    What&apos;s New
                  </DialogTitle>
                  <DialogDescription className='text-sm leading-relaxed'>
                    Recent improvements and features in Roger
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>
          </div>

          <div className='flex-1 min-h-0 overflow-y-auto bg-muted/30 px-4 py-4 sm:px-6 sm:py-5'>
            {loading ? (
              <div className='flex flex-col items-center justify-center gap-3 py-16 text-sm text-muted-foreground'>
                <div className='flex h-12 w-12 items-center justify-center rounded-full bg-background shadow-sm ring-1 ring-border/60'>
                  <Loader2 className='h-5 w-5 animate-spin text-primary' />
                </div>
                Loading updates…
              </div>
            ) : updates.length === 0 ? (
              <div className='flex flex-col items-center justify-center gap-2 py-16 text-center'>
                <div className='flex h-12 w-12 items-center justify-center rounded-full bg-background shadow-sm ring-1 ring-border/60'>
                  <Sparkles
                    className='h-5 w-5 text-muted-foreground'
                    aria-hidden='true'
                  />
                </div>
                <p className='text-sm text-muted-foreground'>
                  No recent updates to show.
                </p>
              </div>
            ) : (
              <ul className='space-y-4'>
                {updates.map(update => (
                  <li key={productUpdateRowKey(update)}>
                    <MarkdownContent
                      variant='card'
                      content={update.markdown}
                      imageUrls={imageUrls}
                      emptyPlaceholder='No content for this update.'
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>

          <DialogFooter className='shrink-0 border-t bg-background px-6 py-4 sm:justify-end'>
            <Button onClick={() => setOpen(false)}>Got it</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ProductUpdatesButton;
